import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

type LogType = 'app' | 'error';
type LogLevel = '' | 'error' | 'warn' | 'info' | 'debug';

interface LogFileInfo {
  filename: string;
  type: LogType;
  date: string | null;
  sizeBytes: number;
  modifiedAt: string;
}

interface LogEntry {
  timestamp?: string;
  level?: string;
  message: string;
  service?: string;
  stack?: string;
  meta?: Record<string, unknown>;
}

const BackendLogs: React.FC = () => {
  const [files, setFiles] = useState<LogFileInfo[]>([]);
  const [type, setType] = useState<LogType>('app');
  const [date, setDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [limit, setLimit] = useState<number>(500);
  const [loading, setLoading] = useState<boolean>(false);
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [levelFilter, setLevelFilter] = useState<LogLevel>('');
  const [from, setFrom] = useState<string>(''); // ISO string or empty
  const [to, setTo] = useState<string>('');

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE_URL}/logs/list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFiles(res.data);
      } catch (e: any) {
        setError(e?.response?.data?.message || 'Failed to load log files');
      }
    };
    fetchFiles();
  }, []);

  const dates = useMemo(() => {
    return Array.from(new Set(files.filter(f => f.type === type && f.date).map(f => f.date as string)));
  }, [files, type]);

  const loadEntries = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/logs/read`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          type,
          date,
          limit,
          q: search || undefined,
          level: levelFilter || undefined,
          from: from || undefined,
          to: to || undefined,
        },
      });
      setEntries(res.data.entries || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load log entries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, date, limit, search, levelFilter, from, to]);

  const handleTypeChange = (evt: SelectChangeEvent) => setType(evt.target.value as LogType);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Backend Logs</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="type-label">Type</InputLabel>
            <Select labelId="type-label" label="Type" value={type} onChange={handleTypeChange}>
              <MenuItem value="app">App</MenuItem>
              <MenuItem value="error">Error</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="date-label">Date</InputLabel>
            <Select labelId="date-label" label="Date" value={date} onChange={(e) => setDate(e.target.value)}>
              {dates.length === 0 && (
                <MenuItem value={date}>{date}</MenuItem>
              )}
              {dates.map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="level-label">Level</InputLabel>
            <Select labelId="level-label" label="Level" value={levelFilter} onChange={(e) => setLevelFilter(e.target.value as LogLevel)}>
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="warn">Warn</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="debug">Debug</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Search"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="From"
            type="datetime-local"
            size="small"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            sx={{ width: 220 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="datetime-local"
            size="small"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            sx={{ width: 220 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Lines"
            type="number"
            size="small"
            value={limit}
            onChange={(e) => setLimit(Math.max(50, Math.min(5000, parseInt(e.target.value || '0', 10))))}
            sx={{ width: 120 }}
            inputProps={{ min: 50, max: 5000 }}
          />
          <Button variant="outlined" onClick={loadEntries} disabled={loading}>Refresh</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              const url = `${API_BASE_URL}/logs/download?type=${type}&date=${encodeURIComponent(date)}`;
              const a = document.createElement('a');
              a.href = url;
              a.setAttribute('download', `${type}-${date}.log`);
              a.setAttribute('target', '_blank');
              a.click();
            }}
          >
            Download
          </Button>
        </Stack>
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {type.toUpperCase()} {date}
          </Typography>
          <Chip label={`${entries.length} lines`} size="small" />
        </Box>
        <Box sx={{
          bgcolor: 'background.default',
          p: 1,
          borderRadius: 1,
          fontFamily: 'monospace',
          fontSize: 12,
          maxHeight: '70vh',
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
        }}>
          {entries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No log entries.</Typography>
          ) : (
            entries.map((e, idx) => (
              <Box key={idx} sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {e.timestamp} {e.level?.toUpperCase?.()} {e.service ? `[${e.service}]` : ''}
                </Typography>
                <Box>{e.message}</Box>
                {e.stack && (
                  <Box sx={{ color: 'error.main' }}>{e.stack}</Box>
                )}
              </Box>
            ))
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default BackendLogs;


